import { Link } from 'react-router-dom';
import Aurora from '../home/components/Aurora';
import { CursorGlow } from '../home/components/CursorGlow';

export default function WhoWeArePage() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Aurora Background */}
      <div className="fixed inset-0 z-0">
        <Aurora 
          colorStops={['#ed66ff', '#b19eef', '#5227ff']}
          amplitude={1.2}
          blend={0.6}
          speed={0.8}
        />
      </div>

      {/* Global Effects */}
      <CursorGlow />

      {/* Content */}
      <div className="relative z-10">
        <div className="absolute inset-0 bg-black/30"></div>
        
        <div className="relative z-10 pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24">
          <div className="container mx-auto px-4 sm:px-6">
            {/* Header */}
            <div className="text-center mb-12 sm:mb-16 md:mb-20">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extralight text-white mb-4 sm:mb-6 tracking-[0.1em] px-4">
                WHO <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">WE ARE</span>
              </h1>
              <p className="text-gray-400 text-base sm:text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed px-4">
                Our mission, vision, story, and commitment to excellence
              </p>
            </div>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto space-y-12 sm:space-y-16">
              {/* CEO Section */}
              <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6 sm:p-8 md:p-10 overflow-hidden">
                <div className="flex flex-col md:flex-row items-center gap-8 sm:gap-10">
                  {/* CEO Image */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl blur-xl opacity-30"></div>
                      <img 
                        src="https://static.readdy.ai/image/8ff5f191f084c4fb11b85d16f9e2729c/33b5d643d6227bb2d88691a7d0336d8b.png"
                        alt="CEO of Tehk Zone"
                        className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 object-cover rounded-2xl border-2 border-purple-500/50"
                      />
                    </div>
                  </div>

                  {/* CEO Bio */}
                  <div className="flex-1">
                    <div className="mb-4 sm:mb-6">
                      <h2 className="text-2xl sm:text-3xl font-light text-white mb-2 tracking-wide">
                        Message from Our CEO
                      </h2>
                      <div className="flex items-center gap-2 text-purple-400">
                        <i className="ri-user-star-line text-lg sm:text-xl"></i>
                        <span className="text-xs sm:text-sm font-light">Chief Executive Officer</span>
                      </div>
                    </div>
                    
                    <blockquote className="relative">
                      <div className="absolute -top-4 -left-2 text-4xl sm:text-6xl text-purple-400/30 font-serif">"</div>
                      <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-3 sm:mb-4 pl-4 sm:pl-6">
                        At Tehk Zone, we believe in the power of innovation and collaboration to transform businesses. Our commitment is to deliver world-class solutions that not only meet but exceed our clients' expectations.
                      </p>
                      <p className="text-gray-300 text-base sm:text-lg leading-relaxed pl-4 sm:pl-6">
                        Every project we undertake is driven by our core values of excellence, integrity, and sustainable growth. Together with our talented team and trusted partners, we continue to shape the future of marketing and technology.
                      </p>
                      <div className="absolute -bottom-2 right-0 text-4xl sm:text-6xl text-purple-400/30 font-serif">"</div>
                    </blockquote>
                  </div>
                </div>
              </div>

              {/* Mission */}
              <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 md:p-10 hover:border-purple-500/50 transition-all duration-500">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-2xl border border-purple-500/30">
                      <i className="ri-compass-3-line text-2xl sm:text-3xl text-purple-400"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl font-light text-white mb-4 sm:mb-6 tracking-wide">
                      Our Mission
                    </h2>
                    <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                      To provide world-class marketing, media, and technology solutions that empower individuals, businesses, and organizations to achieve growth, visibility, and measurable success. We are committed to delivering innovative, practical, and results-driven services that transform how our clients connect with their audiences and achieve their goals.
                    </p>
                  </div>
                </div>
              </div>

              {/* Vision */}
              <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 md:p-10 hover:border-purple-500/50 transition-all duration-500">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-2xl border border-purple-500/30">
                      <i className="ri-eye-line text-2xl sm:text-3xl text-purple-400"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl font-light text-white mb-4 sm:mb-6 tracking-wide">
                      Our Vision
                    </h2>
                    <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                      To become a leading global brand in marketing and technology, recognized for innovation, trust, and sustainable impact in transforming how businesses and institutions connect with the world. We envision a future where Tehk Zone is synonymous with excellence, creativity, and technological advancement.
                    </p>
                  </div>
                </div>
              </div>

              {/* Story */}
              <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 md:p-10 hover:border-purple-500/50 transition-all duration-500">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-2xl border border-purple-500/30">
                      <i className="ri-book-open-line text-2xl sm:text-3xl text-purple-400"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl font-light text-white mb-4 sm:mb-6 tracking-wide">
                      Our Story
                    </h2>
                    <div className="space-y-4 sm:space-y-6">
                      <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                        Tehk Zone Inc. was founded as an African-American company with official registrations in California, United States, and Nigeria. We were built on the belief that every business deserves access to modern solutions that combine creativity with functionality. Recognizing the growing demand for integrated marketing and technology services, we created a company that delivers both under one roof.
                      </p>
                      <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                        Our strategic presence in both continents allows us to serve a diverse range of clients with a unique perspective that blends global innovation with local market understanding. Over time, Tehk Zone has expanded beyond marketing to become a multi-disciplinary agency, offering end-to-end services such as digital strategy, motion graphics, enterprise software, photography, videography, media management, and the importation of high-tech equipment required by organizations, governments, and businesses.
                      </p>
                      <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                        Our ability to adapt and evolve, combined with our international reach, has cemented our place as a forward-looking brand committed to innovation and long-term impact across borders.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commitment */}
              <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 sm:p-8 md:p-10 hover:border-purple-500/50 transition-all duration-500">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-2xl border border-purple-500/30">
                      <i className="ri-shield-check-line text-2xl sm:text-3xl text-purple-400"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl font-light text-white mb-4 sm:mb-6 tracking-wide">
                      Our Commitment
                    </h2>
                    <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                      At Tehk Zone, our values are not negotiable—they are who we are. We believe that staying true to our values keeps us focused, accountable, and connected to our mission: putting clients first and winning together.
                    </p>
                    <p className="text-gray-400 text-base sm:text-lg leading-relaxed">
                      They remind us that success isn't only about what we achieve, but also about how we achieve it—with integrity, collaboration, and excellence at the center of every step. By choosing Tehk Zone, clients gain a trusted ally dedicated to building strong brands, delivering modern solutions, and shaping the future of business.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-purple-500/30 rounded-2xl p-8 sm:p-10 md:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-4 sm:mb-6 tracking-wide">
                  Join Us on This Journey
                </h2>
                <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
                  Let's work together to transform your business and achieve sustainable growth in the digital age
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                  <Link 
                    to="/services"
                    className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-purple-500 text-white text-sm sm:text-base rounded-xl hover:bg-purple-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Explore Our Services
                  </Link>
                  <Link 
                    to="/contact"
                    className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm sm:text-base rounded-xl hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
